import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRefrescarDatos } from "../lib/consultas";
import { mgAGramos } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Panel } from "../componentes/piezas";
import BuscadorSocios, { type SocioResumen } from "../componentes/BuscadorSocios";
import { useSesion } from "../lib/sesion";
import { useIdioma, IDIOMAS } from "../lib/idioma";

interface Ficha {
  socio: {
    id: string;
    numeroSocio: number;
    nombre: string;
    apellidos: string;
    documentoTipo: string;
    documentoNumero: string;
    fechaNacimiento: string;
    telefono: string | null;
    email: string | null;
    direccion: string | null;
    ciudad: string | null;
    cp: string | null;
    limiteDiarioMg: number | null;
    limiteMensualMg: number | null;
    consumoPrevistoMensualMg: number;
    notas: string | null;
    idioma: string | null;
    consentimientoRgpd: boolean;
    avalista: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
    tipoSocio: { id: string; nombre: string } | null;
  };
}

/** Edición de la ficha: los mismos datos del alta más los límites propios del socio. */
export default function SocioEditar() {
  const { t } = useIdioma();
  const { id = "" } = useParams();
  const navegar = useNavigate();
  const { club } = useSesion();
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [avalista, setAvalista] = useState<SocioResumen | null>(null);
  const [cargado, setCargado] = useState(false);

  const ficha = useQuery({ queryKey: ["socio", id], queryFn: () => api<Ficha>(`/socios/${id}`) });
  const tipos = useQuery({
    queryKey: ["tipos-socio"],
    queryFn: () => api<{ id: string; nombre: string; activo: boolean }[]>("/tipos-socio"),
  });

  const [datos, setDatos] = useState({
    tipoSocioId: "",
    nombre: "",
    apellidos: "",
    documentoTipo: "DNI",
    documentoNumero: "",
    fechaNacimiento: "",
    telefono: "",
    email: "",
    direccion: "",
    ciudad: "",
    cp: "",
    limiteDiarioG: "",
    limiteMensualG: "",
    consumoPrevistoMensualG: "0",
    notas: "",
    idioma: "",
    consentimientoRgpd: false,
  });

  // Se rellena una sola vez al llegar la ficha, para no pisar lo que el usuario escribe.
  useEffect(() => {
    if (!ficha.data || cargado) return;
    const s = ficha.data.socio;
    setDatos({
      tipoSocioId: s.tipoSocio?.id ?? "",
      nombre: s.nombre,
      apellidos: s.apellidos,
      documentoTipo: s.documentoTipo,
      documentoNumero: s.documentoNumero,
      fechaNacimiento: s.fechaNacimiento.slice(0, 10),
      telefono: s.telefono ?? "",
      email: s.email ?? "",
      direccion: s.direccion ?? "",
      ciudad: s.ciudad ?? "",
      cp: s.cp ?? "",
      limiteDiarioG: s.limiteDiarioMg == null ? "" : String(mgAGramos(s.limiteDiarioMg)),
      limiteMensualG: s.limiteMensualMg == null ? "" : String(mgAGramos(s.limiteMensualMg)),
      consumoPrevistoMensualG: String(mgAGramos(s.consumoPrevistoMensualMg)),
      notas: s.notas ?? "",
      idioma: s.idioma ?? "",
      consentimientoRgpd: s.consentimientoRgpd,
    });
    if (s.avalista) {
      setAvalista({
        id: s.avalista.id,
        numeroSocio: s.avalista.numeroSocio,
        nombre: s.avalista.nombre,
        apellidos: s.avalista.apellidos,
        documentoNumero: "",
        estado: "ACTIVO",
        fotoUrl: null,
        saldoCentimos: 0,
      });
    }
    setCargado(true);
  }, [ficha.data, cargado]);

  const cambiar = (campo: string, valor: string | boolean) => setDatos((d) => ({ ...d, [campo]: valor }));

  const refrescarDatos = useRefrescarDatos();

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await api(`/socios/${id}`, {
        metodo: "PATCH",
        cuerpo: {
          ...datos,
          telefono: datos.telefono || null,
          email: datos.email || null,
          direccion: datos.direccion || null,
          ciudad: datos.ciudad || null,
          cp: datos.cp || null,
          notas: datos.notas || null,
          idioma: datos.idioma || null,
          // Vacío significa "usa el límite del club"
          limiteDiarioG: datos.limiteDiarioG === "" ? null : Number(datos.limiteDiarioG),
          limiteMensualG: datos.limiteMensualG === "" ? null : Number(datos.limiteMensualG),
          consumoPrevistoMensualG: Number(datos.consumoPrevistoMensualG || 0),
          tipoSocioId: datos.tipoSocioId || null,
          avalistaId: avalista?.id ?? null,
        },
      });
      refrescarDatos();
      navegar(`/socios/${id}`);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("se.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  if (ficha.isLoading || !ficha.data || !cargado) return <Cargando />;
  const socio = ficha.data.socio;

  const limiteDiarioClub = club?.limiteDiarioMg != null ? mgAGramos(club.limiteDiarioMg) : undefined;
  const limiteMensualClub = club?.limiteMensualMg != null ? mgAGramos(club.limiteMensualMg) : undefined;

  return (
    <form onSubmit={guardar}>
      <Cabecera
        titulo={t("se.titulo", { socio: `${socio.nombre} ${socio.apellidos}` })}
        descripcion={t("se.descripcion", { numero: socio.numeroSocio })}
      >
        <button type="button" className="boton secundario" onClick={() => navegar(`/socios/${id}`)}>
          {t("g.cancelar")}
        </button>
        <button className="boton" disabled={enviando}>
          {enviando ? t("g.guardando") : t("se.guardarCambios")}
        </button>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla dos">
        <Panel titulo={t("se.datosPersonales")}>
          <div className="linea-campos">
            <Campo etiqueta={t("se.nombre")}>
              <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} required autoFocus />
            </Campo>
            <Campo etiqueta={t("se.apellidos")}>
              <input value={datos.apellidos} onChange={(e) => cambiar("apellidos", e.target.value)} required />
            </Campo>
          </div>

          <div className="linea-campos">
            <Campo etiqueta={t("se.tipoDocumento")}>
              <select value={datos.documentoTipo} onChange={(e) => cambiar("documentoTipo", e.target.value)}>
                <option value="DNI">DNI</option>
                <option value="NIE">NIE</option>
                <option value="PASAPORTE">{t("sn.pasaporte")}</option>
              </select>
            </Campo>
            <Campo etiqueta={t("se.numero")}>
              <input
                value={datos.documentoNumero}
                onChange={(e) => cambiar("documentoNumero", e.target.value.toUpperCase())}
                required
              />
            </Campo>
          </div>

          <Campo etiqueta={t("se.nacimiento")}>
            <input
              type="date"
              value={datos.fechaNacimiento}
              onChange={(e) => cambiar("fechaNacimiento", e.target.value)}
              required
            />
          </Campo>

          <div className="linea-campos">
            <Campo etiqueta={t("sn.telefono")}>
              <input value={datos.telefono} onChange={(e) => cambiar("telefono", e.target.value)} />
            </Campo>
            <Campo etiqueta={t("sn.email")}>
              <input type="email" value={datos.email} onChange={(e) => cambiar("email", e.target.value)} />
            </Campo>
          </div>

          <Campo etiqueta={t("sn.direccion")}>
            <input value={datos.direccion} onChange={(e) => cambiar("direccion", e.target.value)} />
          </Campo>

          <div className="linea-campos">
            <Campo etiqueta={t("sn.ciudad")}>
              <input value={datos.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} />
            </Campo>
            <Campo etiqueta={t("sn.cp")}>
              <input value={datos.cp} onChange={(e) => cambiar("cp", e.target.value)} />
            </Campo>
          </div>
        </Panel>

        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("se.limitesPropios")}>
            <p className="suave pequeno" style={{ marginTop: 0 }}>
              {limiteDiarioClub !== undefined && limiteMensualClub !== undefined
                ? t("se.sinLimitesConCifras", { diario: limiteDiarioClub, mensual: limiteMensualClub })
                : t("se.sinLimites")}
            </p>
            <div className="linea-campos">
              <Campo etiqueta={t("se.limiteDiario")}>
                <input
                  type="number"
                  min={0}
                  step="0.5"
                  placeholder={
                    limiteDiarioClub !== undefined ? t("se.delClub", { cantidad: limiteDiarioClub }) : ""
                  }
                  value={datos.limiteDiarioG}
                  onChange={(e) => cambiar("limiteDiarioG", e.target.value)}
                />
              </Campo>
              <Campo etiqueta={t("se.limiteMensual")}>
                <input
                  type="number"
                  min={0}
                  step="1"
                  placeholder={
                    limiteMensualClub !== undefined ? t("se.delClub", { cantidad: limiteMensualClub }) : ""
                  }
                  value={datos.limiteMensualG}
                  onChange={(e) => cambiar("limiteMensualG", e.target.value)}
                />
              </Campo>
            </div>
            <Campo etiqueta={t("sn.consumo")}>
              <input
                type="number"
                min={0}
                step={1}
                value={datos.consumoPrevistoMensualG}
                onChange={(e) => cambiar("consumoPrevistoMensualG", e.target.value)}
              />
            </Campo>
          </Panel>

          <Panel titulo={t("se.asociacion")}>
            {tipos.data && tipos.data.length > 0 && (
              <Campo etiqueta={t("se.tipoDeSocio")}>
                <select value={datos.tipoSocioId} onChange={(e) => cambiar("tipoSocioId", e.target.value)}>
                  <option value="">{t("se.sinTipo")}</option>
                  {tipos.data
                    .filter((t) => t.activo || t.id === datos.tipoSocioId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre}
                      </option>
                    ))}
                </select>
              </Campo>
            )}

            <Campo etiqueta={t("se.avalista")}>
              {avalista ? (
                <div className="fila separada" style={{ padding: "8px 0" }}>
                  <span>
                    #{avalista.numeroSocio} {avalista.nombre} {avalista.apellidos}
                  </span>
                  <button type="button" className="boton fantasma" onClick={() => setAvalista(null)}>
                    {t("sn.quitar")}
                  </button>
                </div>
              ) : (
                <BuscadorSocios alElegir={setAvalista} marcador={t("se.buscarAvalista")} />
              )}
            </Campo>

            <label className="casilla">
              <input
                type="checkbox"
                checked={datos.consentimientoRgpd}
                onChange={(e) => cambiar("consentimientoRgpd", e.target.checked)}
              />
              <span>{t("se.rgpd")}</span>
            </label>

            {/* En blanco = el del club. Se pone aqui porque al socio de fuera
                se lo pregunta quien esta en el mostrador, no el socio. */}
            <Campo etiqueta={t("sn.idioma")} ayuda={t("sn.idiomaAyuda")}>
              <select value={datos.idioma} onChange={(e) => cambiar("idioma", e.target.value)}>
                <option value="">{t("sn.idiomaDelClub")}</option>
                {IDIOMAS.map((i) => (
                  <option key={i.codigo} value={i.codigo} lang={i.codigo}>
                    {i.nombre}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta={t("sn.notas")}>
              <textarea rows={4} value={datos.notas} onChange={(e) => cambiar("notas", e.target.value)} />
            </Campo>
          </Panel>
        </div>
      </div>
    </form>
  );
}
